import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building,
  Zap,
  ChevronDown,
  RefreshCw,
  DownloadCloud,
  PlugZap,
} from "lucide-react";
import { toast } from "sonner";

export default function HostawayCard() {
  const [state, setState] = useState({
    connected: false,
    account_id: "",
    api_key_last4: "",
    last_sync: null,
    last_listings_count: 0,
    last_reservations_count: 0,
  });
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncingL, setSyncingL] = useState(false);
  const [syncingR, setSyncingR] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/hostaway");
      setState(data);
      setAccountId(data.account_id || "");
    } catch {
      /* silent */
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!accountId.trim() && !apiKey.trim()) {
      toast.error("Enter your Hostaway Account ID and API Key");
      return;
    }
    setBusy(true);
    try {
      const body = { account_id: accountId.trim() };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      await api.put("/settings/hostaway", body);
      toast.success("Saved");
      setApiKey("");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/hostaway/test");
      toast.success(
        `Connected · ${data.listings_available ?? 0} listings visible in Hostaway`,
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Disconnect Hostaway? Your imported listings and bookings will stay.",
      )
    )
      return;
    try {
      await api.delete("/settings/hostaway");
      toast.success("Hostaway disconnected");
      setApiKey("");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const syncListings = async () => {
    setSyncingL(true);
    try {
      const { data } = await api.post("/hostaway/sync/listings");
      toast.success(`Imported ${data.imported} listing(s)`);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSyncingL(false);
    }
  };

  const syncReservations = async () => {
    setSyncingR(true);
    try {
      const { data } = await api.post("/hostaway/sync/reservations");
      toast.success(
        `${data.bookings_imported} new booking(s) · ${data.contacts_created} new guest(s)`,
      );
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSyncingR(false);
    }
  };

  return (
    <form onSubmit={save} className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Building className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Channel manager</div>
          <div className="font-serif text-2xl">Hostaway</div>
        </div>
        {state.connected && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium bg-teal-50 text-teal-800 border border-teal-100 rounded-full px-2.5 py-1">
            <Zap className="w-3 h-3" /> Connected · key ends {state.api_key_last4}
          </span>
        )}
      </div>

      <div className="ns-hairline my-6" />

      <div className="grid gap-4 max-w-xl">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="ns-label">Account ID</Label>
            <Input
              className="mt-2 h-11 bg-white font-mono"
              placeholder="e.g. 12345"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
          </div>
          <div>
            <Label className="ns-label">API Key</Label>
            <Input
              type="password"
              className="mt-2 h-11 bg-white font-mono"
              placeholder={
                state.connected
                  ? `••••••••••••${state.api_key_last4} (paste a new key to replace)`
                  : "Paste from Hostaway Marketplace → Public API"
              }
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
        </div>
        <div className="text-[11px] text-slate-400 -mt-1">
          Both values stay server-side and are never shown back to the client.
        </div>

        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button
            type="submit"
            disabled={busy}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            {busy ? "Saving…" : "Save credentials"}
          </Button>
          {state.connected && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={test}
                disabled={testing}
                className="rounded-full h-11 px-5"
              >
                <PlugZap className="w-4 h-4 mr-1.5" />
                {testing ? "Testing…" : "Test connection"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={disconnect}
                className="rounded-full h-11 px-4 text-slate-500 hover:text-red-600 ml-auto"
              >
                Disconnect
              </Button>
            </>
          )}
        </div>

        {state.connected && (
          <div className="mt-3 bg-stone-50 border border-stone-100 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="ns-label">Sync</div>
                <div className="text-xs text-slate-500 mt-1">
                  Last sync:{" "}
                  {state.last_sync
                    ? new Date(state.last_sync).toLocaleString()
                    : "never"}
                  {state.last_listings_count > 0 &&
                    ` · ${state.last_listings_count} listings`}
                  {state.last_reservations_count > 0 &&
                    ` · ${state.last_reservations_count} reservations`}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={syncListings}
                  disabled={syncingL}
                  className="rounded-full h-10 px-4"
                >
                  <DownloadCloud className="w-4 h-4 mr-1.5" />
                  {syncingL ? "Importing…" : "Import listings"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={syncReservations}
                  disabled={syncingR}
                  className="rounded-full h-10 px-4"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  {syncingR ? "Syncing…" : "Sync reservations"}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong>Import listings</strong> creates or updates a property in
              your CRM for every Hostaway listing (name, address, bedrooms,
              hero photo). <strong>Sync reservations</strong> pulls the last
              500 bookings, matches each guest to an existing contact by email
              (or creates a new one, tagged <code>hostaway</code> plus the
              channel like <code>airbnb</code>), and creates a stay against
              the right unit. Safe to re-run — nothing gets duplicated.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="text-xs text-teal-700 hover:underline mt-2 self-start flex items-center gap-1"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${showHelp ? "rotate-180" : ""}`}
          />
          Where to find your Hostaway Account ID and API Key
        </button>

        {showHelp && (
          <div className="bg-teal-50/40 border border-teal-100 rounded-xl p-5 text-sm text-slate-700 leading-relaxed">
            <ol className="list-decimal ml-5 space-y-2">
              <li>
                Sign in to{" "}
                <a
                  className="text-teal-700 hover:underline"
                  href="https://dashboard.hostaway.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  dashboard.hostaway.com
                </a>
                .
              </li>
              <li>
                Click your avatar (top-right) → <strong>Marketplace</strong>{" "}
                → search for <strong>&quot;Public API&quot;</strong>{" "}
                → <strong>Install</strong>.
              </li>
              <li>
                Hostaway will display an <strong>Account ID</strong> (short
                number) and an <strong>API Key</strong> (long random string).
                Copy both.
              </li>
              <li>
                Paste them above and click <em>Save credentials</em>, then{" "}
                <em>Test connection</em> to confirm.
              </li>
              <li>
                Hit <em>Import listings</em> once, then <em>Sync reservations
                </em> — you&apos;ll see Hostaway listings appear in Properties
                and guests in Contacts within seconds.
              </li>
            </ol>
          </div>
        )}
      </div>
    </form>
  );
}
